import { useContext, useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wrench, X, Bug, Trash2, RotateCcw, Mail, Search } from "lucide-react";
import { EnlightenmentContext } from "@/context/EnlightenmentContext";
import { AuthContext } from "@/context/AuthContext";
import { useErrorSink } from "@/lib/errorSink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Exempt emails that see the QA console — mirrors backend EXEMPT_EMAILS in services/credits.py
const QA_EMAILS = new Set([
  "tmacslaton@gmail.com",
  "sethmichaelking@protonmail.com",
  "hello@andievargas.com",
  "admin@primordeia.com",
]);

// All 34 tier names — mirrors backend/services/prompts.py TIER_NAMES.
// Keeping them client-side avoids a network round-trip when the wrench opens.
const TIER_NAMES = [
  "Seed", "Gah-Vree-eL", "Rah-Fay-eL", "You-Rel", "Rakes-Isle", "Hah-Nile",
  "Zad-Kay-EL", "Kah-May-EL", "Ay-Rass-Nigh", "Oaks-Oh-So", "Ay-Rass-L",
  "Yah-Dahl", "May-Day-L", "Mah-Row", "Nah-Tay-Ah", "Bat-Eye-Larrotol",
  "Lay-Bez-Ez", "Halla-San", "Yay-R",
  "Sandalphon", "Air-Ah-Tahs", "Razel", "Payx-R", "Azap-Azorendez",
  "Eye-Bah-Pohd Bay-Min-O-Dee", "Svet-Gayl-In", "O-Rex-O-Dep",
  "Nay-Yes-Matt", "Doe-Toe-Nayah", "Oh-Duh-May-Pay-Uh", "Chah-Fay",
  "Ay-Eyes-Nur", "Rex-Aches-Lay", "Swear-Rose-Uh",
  // ─── Level 30 — Angel of the Garden ───
  "Oh-Dray-X-Tie", "Yakav-Es-Rayl", "Pay-Row-Aeon", "Dime-Mer", "Eye-Zay-Ner",
];

// Band labels — shown as subtle dividers in the tier list
const TIER_BAND = (idx) => {
  if (idx <= 9) return "Botaneia";
  if (idx <= 26) return "Anamalia";
  if (idx <= 31) return "Noctyra · Holy Soldier";
  if (idx <= 33) return "Primordeia · Armor";
  return "Noctyra · Angel of the Garden";
};

/**
 * QA Console — visible only when signed in with an EXEMPT email.
 * One-click tier jumps for tier-28/29 UI verification (Outfit progress bar,
 * Noctyra garments, action items) without needing curl + DEV_QA_TOKEN.
 */
const QAConsole = () => {
  const { user } = useContext(AuthContext);
  const { sessionId, syncSession, currentTier } = useContext(EnlightenmentContext);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [tab, setTab] = useState("tier");  // "tier" | "errors"
  const [prejumpTier, setPrejumpTier] = useState(null);
  const [tierFilter, setTierFilter] = useState("");
  const sink = useErrorSink();

  const email = (user?.email || "").trim().toLowerCase();
  const isQA = email && QA_EMAILS.has(email);

  // Fetch prejump_tier when modal opens so the Restore button shows the right number.
  // iter-34: prefer the richer `prejump_state.tier` (full step-back snapshot) over the
  // legacy `prejump_tier` field. We also refresh on `currentTier` changes so consecutive
  // jumps in another tab/window keep the Restore button accurate.
  useEffect(() => {
    if (!isQA || !open) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/session/${sessionId}`, { cache: "no-store" });
        if (r.ok) {
          const s = await r.json();
          if (!alive) return;
          const stateTier = s?.prejump_state?.tier;
          if (typeof stateTier === "number") {
            setPrejumpTier(stateTier);
          } else if (typeof s.prejump_tier === "number") {
            setPrejumpTier(s.prejump_tier);
          } else {
            setPrejumpTier(null);
          }
        }
      } catch (err) { console.warn("[QA] prejump fetch failed:", err?.message); }
    })();
    return () => { alive = false; };
  }, [isQA, open, sessionId, currentTier]);

  // Build the filtered tier list — supports searching by tier number ("8", "tier 8")
  // or by name fragment ("seed", "yay-r"). All 34 tiers shown when filter empty.
  // Declared BEFORE the early-return so hook order stays stable across renders.
  const filteredTiers = useMemo(() => {
    const q = tierFilter.trim().toLowerCase();
    const isNumeric = /^\d+$/.test(q);
    return TIER_NAMES.map((name, idx) => ({ idx, name, label: `Tier ${idx + 1} — ${name}`, band: TIER_BAND(idx) }))
      .filter(({ idx, name }) => {
        if (!q) return true;
        // Pure-number input ("8") must match the tier number EXACTLY — not substring
        // (otherwise "8" would also match Tier 18 / Tier 28).
        if (isNumeric) return String(idx + 1) === q;
        if (name.toLowerCase().includes(q)) return true;
        if (`tier ${idx + 1}`.includes(q)) return true;
        return false;
      });
  }, [tierFilter]);

  if (!isQA) return null;

  const errorCount = sink.errorCount;
  const warnCount = sink.warnCount;
  const hasFunky = errorCount > 0 || warnCount > 0;

  const jump = async (tier, opts = {}) => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`${API}/session/${sessionId}/set-tier`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tier,
          reset_progress: opts.preserveProgress ? false : true,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      // Capture the prejump tier so the Restore button shows the correct "→ Tier X"
      // iter-34: read from the rich prejump_state.tier; fall back to legacy prejump_tier
      const stateTier = data?.session?.prejump_state?.tier;
      if (typeof stateTier === "number") {
        setPrejumpTier(stateTier);
      } else if (typeof data.session?.prejump_tier === "number") {
        setPrejumpTier(data.session.prejump_tier);
      }
      await syncSession();
      setMsg(`✓ Jumped to tier ${tier + 1}`);
    } catch (e) {
      const detail = e && (e.message || "failed");
      setMsg(`✗ ${detail === "Failed to fetch" ? "Network error — check your connection" : detail}`);
      setTimeout(() => setMsg(null), 4000);
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`${API}/session/${sessionId}/restore-tier`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.ok) {
        await syncSession();
        setMsg(`✓ Returned to tier ${data.tier + 1}`);
        setPrejumpTier(null);
      } else {
        setMsg(`✗ ${data.error || "nothing to restore"}`);
        setTimeout(() => setMsg(null), 4000);
      }
    } catch (e) {
      setMsg(`✗ ${e?.message || "restore failed"}`);
      setTimeout(() => setMsg(null), 4000);
    } finally {
      setBusy(false);
    }
  };

  const reportError = async (entry) => {
    try {
      const r = await fetch(`${API}/qa/report-error`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entry: { ...entry, ts: entry.ts.toISOString() },
          context: {
            url: window.location.href,
            user_agent: navigator.userAgent,
            session_id: sessionId,
          },
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      setMsg(`✉ Sent to ${email}`);
      setTimeout(() => setMsg(null), 3500);
    } catch (e) {
      setMsg(`✗ ${e?.message || "send failed"}`);
      setTimeout(() => setMsg(null), 4000);
    }
  };

  return (
    <>
      {/* Floating wrench — small, bottom-left, only for QA accounts. Red dot
          appears whenever console.error or console.warn has fired since load. */}
      <motion.button
        type="button"
        onClick={() => { setOpen(true); setTab(hasFunky ? "errors" : "tier"); }}
        whileHover={{ scale: 1.08, rotate: -8 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 left-6 z-[460] w-11 h-11 rounded-full flex items-center justify-center"
        style={{
          background: "linear-gradient(135deg, #3D2A56 0%, #1A1030 100%)",
          color: "#D4A574",
          border: "1.5px solid rgba(212,165,116,0.55)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
        }}
        title={hasFunky ? `QA Console — ${errorCount} error(s), ${warnCount} warning(s)` : "QA Console — exempt email only"}
        data-testid="qa-console-trigger"
      >
        <Wrench size={18} />
        {hasFunky && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.2, 1] }}
            transition={{ duration: 0.35 }}
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold"
            style={{
              background: errorCount > 0 ? "#E63946" : "#F0B429",
              color: "#FFF",
              border: "1.5px solid #1A1030",
              boxShadow: "0 0 8px rgba(230,57,70,0.6)",
            }}
            data-testid="qa-console-funky-dot"
          >
            {errorCount + warnCount > 9 ? "9+" : errorCount + warnCount}
          </motion.span>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[470] flex items-center justify-center px-4"
            style={{ background: "rgba(14, 8, 24, 0.78)", backdropFilter: "blur(8px)" }}
            data-testid="qa-console-modal"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 14 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-sm rounded-3xl p-6"
              style={{
                background: "linear-gradient(135deg, #14091F 0%, #251440 60%, #1A2810 100%)",
                border: "1px solid rgba(212,165,116,0.4)",
                boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
              }}
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10"
                style={{ color: "#D4A574" }}
                aria-label="Close"
                data-testid="qa-console-close"
              >
                <X size={18} />
              </button>

              <div className="text-center mb-4">
                <Wrench className="mx-auto mb-2" size={22} style={{ color: "#D4A574" }} />
                <div className="text-[10px] font-bold uppercase tracking-[0.2em]"
                  style={{ color: "#D4A574", fontFamily: "'Cinzel', serif" }}>
                  QA Console
                </div>
                <p className="text-[10px] mt-1" style={{ color: "#8B7DA8" }}>
                  Signed in as <strong>{email}</strong>
                </p>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{ background: "rgba(255,255,255,0.04)" }}>
                <button
                  onClick={() => setTab("tier")}
                  className="flex-1 py-1.5 rounded-lg text-[10px] font-bold flex items-center justify-center gap-1 transition"
                  style={{
                    background: tab === "tier" ? "linear-gradient(135deg, #4F378B, #7B5EA7)" : "transparent",
                    color: tab === "tier" ? "#FFF" : "#B8A8D8",
                  }}
                  data-testid="qa-tab-tier"
                >
                  <Wrench size={11} /> Tier Jump
                </button>
                <button
                  onClick={() => setTab("errors")}
                  className="flex-1 py-1.5 rounded-lg text-[10px] font-bold flex items-center justify-center gap-1 transition relative"
                  style={{
                    background: tab === "errors" ? "linear-gradient(135deg, #4F378B, #7B5EA7)" : "transparent",
                    color: tab === "errors" ? "#FFF" : "#B8A8D8",
                  }}
                  data-testid="qa-tab-errors"
                >
                  <Bug size={11} /> Errors
                  {hasFunky && (
                    <span className="min-w-[14px] h-[14px] px-1 rounded-full flex items-center justify-center text-[8px] font-bold"
                      style={{ background: errorCount > 0 ? "#E63946" : "#F0B429", color: "#FFF" }}>
                      {errorCount + warnCount}
                    </span>
                  )}
                </button>
              </div>

              {tab === "tier" && (
                <>
                  <h3 className="text-base font-bold mb-2 text-center" style={{ color: "#FFF" }}>
                    Tier Fast-Forward
                  </h3>
                  {/* Restore — only shown when we have a saved prejump_tier */}
                  {prejumpTier !== null && (
                    <button
                      onClick={restore}
                      disabled={busy}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs transition mb-2"
                      style={{
                        background: "linear-gradient(135deg, rgba(123,201,123,0.18), rgba(123,94,167,0.18))",
                        border: "1px solid rgba(123,201,123,0.45)",
                        color: "#E8F8E8",
                        opacity: busy ? 0.5 : 1,
                      }}
                      data-testid="qa-restore-tier"
                    >
                      <span className="flex items-center gap-1.5">
                        <RotateCcw size={11} /> Return to where I was
                      </span>
                      <span className="text-[10px]" style={{ color: "#7BC97B" }}>
                        → Tier {prejumpTier + 1}
                      </span>
                    </button>
                  )}
                  {/* Filter box — search by tier number or angel name */}
                  <div className="relative mb-2">
                    <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2"
                      style={{ color: "#8B7DA8" }} />
                    <input
                      type="text"
                      value={tierFilter}
                      onChange={(e) => setTierFilter(e.target.value)}
                      placeholder="Search tier 1-34 or angel name…"
                      className="w-full pl-7 pr-2 py-1.5 rounded-lg text-[11px]"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(212,165,116,0.25)",
                        color: "#FFF8E7",
                        outline: "none",
                      }}
                      data-testid="qa-tier-filter"
                    />
                  </div>
                  <div className="space-y-1 max-h-[40vh] overflow-y-auto pr-1"
                    data-testid="qa-tier-list">
                    {filteredTiers.length === 0 && (
                      <div className="text-center text-[10px] italic py-4" style={{ color: "#8B7DA8" }}>
                        No tier matches &ldquo;{tierFilter}&rdquo;
                      </div>
                    )}
                    {filteredTiers.map(({ idx, label, band }, listIdx) => {
                      const prevBand = listIdx > 0 ? filteredTiers[listIdx - 1].band : null;
                      const showBand = band !== prevBand;
                      const isCurrent = currentTier === idx;
                      return (
                        <div key={idx}>
                          {showBand && (
                            <div className="text-[8px] uppercase tracking-[0.25em] font-bold pt-1.5 pb-0.5 px-1"
                              style={{ color: "#D4A574", opacity: 0.7 }}>
                              {band}
                            </div>
                          )}
                          <button
                            onClick={() => jump(idx)}
                            disabled={busy}
                            className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-[11px] transition hover:scale-[1.01]"
                            style={{
                              background: isCurrent
                                ? "linear-gradient(135deg, rgba(212,165,116,0.30), rgba(123,94,167,0.28))"
                                : "rgba(212,165,116,0.08)",
                              border: isCurrent
                                ? "1px solid rgba(212,165,116,0.65)"
                                : "1px solid rgba(212,165,116,0.20)",
                              color: "#FFF8E7",
                              opacity: busy ? 0.5 : 1,
                            }}
                            data-testid={`qa-jump-tier-${idx}`}
                          >
                            <span>{label}</span>
                            <span className="text-[9px]" style={{ color: isCurrent ? "#FFE5A8" : "#B8A8D8" }}>
                              {isCurrent ? "you are here" : `→ ${idx + 1}`}
                            </span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {msg && (
                    <div className="text-center mt-3 text-[11px] font-semibold"
                      style={{ color: msg.startsWith("✓") ? "#7BC97B" : "#E07B7B" }}
                      data-testid="qa-console-status">
                      {msg}
                    </div>
                  )}
                  <div className="text-[9px] italic mt-3 text-center" style={{ color: "#8B7DA8" }}>
                    Jumping resets blueberries + cycle. Your first jump saves where you
                    were — click <strong>&ldquo;Return to where I was&rdquo;</strong> to come back.
                  </div>
                </>
              )}

              {tab === "errors" && (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold" style={{ color: "#FFF" }}>
                      Runtime Log
                    </h3>
                    <button
                      onClick={sink.clear}
                      disabled={sink.count === 0}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition"
                      style={{
                        background: "rgba(230,57,70,0.18)",
                        color: "#FFF",
                        opacity: sink.count === 0 ? 0.4 : 1,
                      }}
                      data-testid="qa-errors-clear"
                    >
                      <Trash2 size={10} /> Clear
                    </button>
                  </div>
                  {sink.count === 0 ? (
                    <div className="text-center py-8 text-xs italic" style={{ color: "#7BC97B" }}
                      data-testid="qa-errors-empty">
                      ✓ No errors or warnings since load.
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1"
                      data-testid="qa-errors-list">
                      {sink.entries.map((e) => (
                        <div key={e.id} className="rounded-lg p-2 text-[10px]"
                          style={{
                            background: e.level === "error" ? "rgba(230,57,70,0.12)" : "rgba(240,180,41,0.10)",
                            border: `1px solid ${e.level === "error" ? "rgba(230,57,70,0.35)" : "rgba(240,180,41,0.35)"}`,
                          }}>
                          <div className="flex items-center justify-between mb-0.5 gap-2">
                            <span className="font-bold uppercase tracking-wider"
                              style={{ color: e.level === "error" ? "#FF8B95" : "#F0B429" }}>
                              {e.level} · {e.source}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => reportError(e)}
                                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold hover:scale-105 transition"
                                style={{ background: "rgba(212,165,116,0.25)", color: "#FFF8E7" }}
                                title="Email this stack trace to my account"
                                data-testid={`qa-error-send-${e.id}`}
                              >
                                <Mail size={9} /> Send to me
                              </button>
                              <span style={{ color: "#8B7DA8" }}>
                                {e.ts.toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                          <div className="whitespace-pre-wrap break-words leading-tight"
                            style={{ color: "#E8E0F8", fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
                            {e.message}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-[9px] italic mt-3 text-center" style={{ color: "#8B7DA8" }}>
                    Captures the last 50 console.error / console.warn / window.error events.
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QAConsole;
